import {
  type CodeOutcome,
  type ItemKind,
  newScanAttemptId,
  type Scan,
  type ScanCode,
} from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import { codeOutcomeText } from "../primitives/barcode";
import { isScanning } from "../primitives/review-row";
import { initialScanProgress, type ScanProgress } from "../primitives/scan-progress";
import { shopEntryId } from "../primitives/workspace";

/** Streams at once per session, as iOS: a browser keeps about six connections per host. */
export const SESSION_STREAMS = 5;
/** How long a scan whose stream ended early is left before it is read again, while the pipeline still has it. */
export const SETTLE_READ_MS = 4000;
/** How long a landed card waits for the read of its list before the tray calls it ready anyway. */
export const LIST_WAIT_MS = 2500;
/** How long a card discarded with its fate unknown is still read: past the backend's orphan sweep, with room. */
export const DISCARDED_READ_MS = 10 * 60_000;
/**
 * How long a card's stream is listened to after its first frame before its scan is read instead: longer than any scan
 * takes (the backend's own watch of a resent attempt gives up at two minutes), and the client's recovery of a cut stream
 * polls a scan whose pipeline died after its reading for good.
 */
export const STREAM_GIVES_UP_MS = 3 * 60_000;
/**
 * How long a send waits for its stream's first frame: the photos' upload is on this clock, and fetch says nothing of an
 * upload's progress, so a slow uplink needs room that a send which never began does not get.
 */
export const STREAM_STARTS_WITHIN_MS = 10 * 60_000;

/** Whether a stream has answered: every frame moves a stage off where a send starts them all. */
const streamBegun = (progress: ScanProgress): boolean =>
  Object.values(progress.stages).some((stage) => stage !== "pending");

/** A stream the server refused before it began, with the server's sentence: the card fails with it. */
export class ScanRefused extends Error {}

export type CaptureStatus = "waiting" | "running" | "failed" | "ready";

/** One card shot or imported in a session, as iOS's tray shows it. */
export interface Capture {
  readonly id: string;
  /** Null for a barcode looked up alone, until a photo follows it. */
  readonly front: Blob | null;
  readonly back: Blob | null;
  readonly previewUrl: string | null;
  /** The session's kind when it was taken; null lets the scanner decide. */
  readonly kind: ItemKind | null;
  /** A barcode typed, sent by a gun, or read off the picture. */
  readonly codes: readonly ScanCode[];
  /** Its barcode named no item: why, while it waits for a photo (its next picture, on its attempt). */
  readonly codeOutcome: CodeOutcome | null;
  readonly status: CaptureStatus;
  readonly progress: ScanProgress;
  /** Why it failed, for the row that says so. */
  readonly error: string | null;
  /** Failed with its scan's fate unknown (the read after an early end failed): Retry reads before it sends. */
  readonly unsure: boolean;
  /**
   * The attempt the server knows the card by: a send of the same attempt finds the scan it made rather than making
   * another. Kept by Retry, and new only once the server ended its scan in an error or rejected it.
   */
  readonly attemptId: string;
}

/** The scan a capture made, once the server named it. */
export const scanIdOf = (capture: Capture): string | null =>
  capture.progress.scanId ?? capture.progress.result?.id ?? null;

/** Landed with no card recognised in it: listed apart, with the scans that cannot be reviewed. */
export const isUnrecognised = (capture: Capture): boolean =>
  capture.status === "ready" && capture.progress.result?.recognitionOutcome === "no_match";

/** A session's cards with those a page still shows after the session let them go (`kept`), newest first. */
export function withKept(captures: readonly Capture[], kept: ReadonlyMap<string, Capture>): Capture[] {
  const live = new Set(captures.map((c) => c.id));
  const gone = [...kept.values()].filter((c) => !live.has(c.id));
  if (gone.length === 0) return [...captures];
  const sent = (c: Capture) => Number(c.id.slice(c.id.lastIndexOf("-") + 1));
  return [...captures, ...gone].sort((a, b) => sent(b) - sent(a));
}

/** A front shot in Front & back, waiting for its back. */
export interface HeldFront {
  readonly picture: Blob;
  readonly previewUrl: string;
  readonly codes: readonly ScanCode[];
}

/** Where a session's scans land: the shop, its counter, and the customer scanned for. */
export interface ScanScope {
  readonly shopId: string;
  readonly locationId: string | null;
  readonly customerId: string | null;
}

/** Sends one card and reports its stages; resolves with the stream's last state and throws when the stream drops. */
export type CaptureRunner = (
  scope: ScanScope,
  images: {
    front: Blob | null;
    back: Blob | null;
    attemptId: string;
    kind: ItemKind | null;
    codes: readonly ScanCode[];
  },
  onProgress: (progress: ScanProgress) => void,
  signal: AbortSignal,
) => Promise<ScanProgress>;

/** Reads one scan: "gone" when the shop has none by that id; throws when it cannot tell. */
export type ScanReader = (scope: ScanScope, scanId: string) => Promise<Scan | "gone">;

export interface ScanSessionDeps {
  run: CaptureRunner;
  read: ScanReader;
  /**
   * A scan landed: the lists it shows on are read again, and its capture turns ready once that read answers (or after
   * `LIST_WAIT_MS`), as iOS's finish waits for its list. Null from an older backend.
   */
  landed: (scope: ScanScope, scan: Scan | null) => Promise<void> | void;
  /** A capture failed: the lists are read again, since a copy still being scanned may have held a card back. */
  failed?: (scope: ScanScope) => void;
  now?: () => Date;
  newAttemptId?: () => string;
  /** Object URLs for the tray; injectable because tests run without a DOM. */
  previewUrl?: (picture: Blob) => string;
  revokePreview?: (url: string) => void;
  streams?: number;
}

/**
 * The cards of one scanning context (a counter, or a customer at it), as iOS's capture model: each card is sent as
 * soon as it is shot, five at a time, oldest first, and listed newest first. A card the stream lost track of is
 * settled by reading its scan, so a Retry never sends one that landed. A session outlives the scanner: what is still
 * on its way goes on, and shows on Review or the customer's page; what is ready leaves when the scanner closes, or
 * as it lands with none open.
 */
export class ScanSession extends ChangeNotifier {
  readonly scope: ScanScope;
  readonly #deps: Required<Omit<ScanSessionDeps, "previewUrl" | "revokePreview" | "streams">> & {
    previewUrl: (picture: Blob) => string;
    revokePreview: (url: string) => void;
    streams: number;
  };
  /** Oldest first, the order they are sent in. */
  #captures: Capture[] = [];
  #heldFront: HeldFront | null = null;
  #frontAndBack = false;
  #kind: ItemKind | null = null;
  #streaming = 0;
  #scanners = 0;
  /** Cards landed whose list read has not answered: Done keeps them, as it keeps what lands after it. */
  readonly #unlisted = new Set<string>();
  /** Pictures of cards let go with no scanner open, which a page may still show on a row: let go at the next opening. */
  #letGone: string[] = [];
  /** Cards of unknown fate a list prompted a read of: one read each, so one that fails leaves the card to Retry. */
  readonly #relisted = new Set<string>();
  #nextId = 1;
  readonly #listening = new Map<string, AbortController>();
  readonly #timers = new Set<ReturnType<typeof setTimeout>>();
  #disposed = false;

  constructor(scope: ScanScope, deps: ScanSessionDeps) {
    super();
    this.scope = scope;
    this.#deps = {
      run: deps.run,
      read: deps.read,
      landed: deps.landed,
      failed: deps.failed ?? (() => undefined),
      now: deps.now ?? (() => new Date()),
      newAttemptId: deps.newAttemptId ?? newScanAttemptId,
      previewUrl: deps.previewUrl ?? ((picture) => URL.createObjectURL(picture)),
      revokePreview: deps.revokePreview ?? ((url) => URL.revokeObjectURL(url)),
      streams: deps.streams ?? SESSION_STREAMS,
    };
  }

  /** Newest first, as the tray lists them. */
  get captures(): readonly Capture[] {
    return [...this.#captures].reverse();
  }

  /** What still shows on Review or the customer's page: every capture not ready, newest first. */
  get unfinished(): readonly Capture[] {
    return this.captures.filter((c) => c.status !== "ready");
  }

  /** Landed and waiting on review; one with no card recognised in it is not. */
  get readyCount(): number {
    return this.#captures.filter((c) => c.status === "ready" && !isUnrecognised(c)).length;
  }

  get unrecognisedCount(): number {
    return this.#captures.filter(isUnrecognised).length;
  }

  /** In Front & back, the front shot waiting for its back, with its preview. */
  get heldFront(): HeldFront | null {
    return this.#heldFront;
  }

  /** Front & back or Front only, kept between openings, as iOS. */
  get frontAndBack(): boolean {
    return this.#frontAndBack;
  }

  /** The kind every card taken from now on is sent as; null lets the scanner decide. Kept between openings. */
  get kind(): ItemKind | null {
    return this.#kind;
  }

  setKind(kind: ItemKind | null): void {
    if (kind === this.#kind) return;
    this.#kind = kind;
    this.notifyListeners();
  }

  /** The oldest barcode that named nothing, waiting for its photo. */
  get awaitingPhoto(): Capture | null {
    return this.#captures.find((c) => c.codeOutcome !== null) ?? null;
  }

  /** Switching drops a front waiting for its back, as iOS. */
  setFrontAndBack(on: boolean): void {
    if (on === this.#frontAndBack) return;
    this.#frontAndBack = on;
    this.#dropHeldFront();
    this.notifyListeners();
  }

  /** A card's picture, shot or imported. Front only sends it; Front & back holds the first and sends it with the next. */
  add(picture: Blob, codes: readonly ScanCode[] = []): void {
    if (this.#disposed) return;
    if (!this.#frontAndBack) {
      this.#enqueue(picture, null, codes);
      return;
    }
    const front = this.#heldFront;
    if (front === null) {
      this.#heldFront = { picture, previewUrl: this.#deps.previewUrl(picture), codes };
      this.notifyListeners();
      return;
    }
    this.#heldFront = null;
    // A box's barcode is often on its back.
    this.#enqueue(front.picture, picture, front.codes.length > 0 ? front.codes : codes, front.previewUrl);
  }

  /** A barcode with no picture: a sealed box it names lands as any card does; one it does not waits for its photo. */
  lookUp(codes: readonly ScanCode[]): void {
    if (this.#disposed || codes.length === 0) return;
    this.#push({ front: null, back: null, previewUrl: null, codes });
  }

  discardHeldFront(): void {
    if (this.#heldFront === null) return;
    this.#dropHeldFront();
    this.notifyListeners();
  }

  /** A failed card again: sent, or, when its scan's fate is unknown, read first and sent only if it did not land. */
  retry(id: string): void {
    const capture = this.#find(id);
    if (capture?.status !== "failed") return;
    const scanId = capture.progress.scanId;
    this.#relisted.delete(id);
    if (capture.unsure && scanId !== null) {
      this.#patch(id, { status: "running", error: null, unsure: false });
      void this.#settle(id, scanId, true);
      return;
    }
    this.#patch(id, {
      status: "waiting",
      progress: initialScanProgress(),
      error: null,
      unsure: false,
      codeOutcome: null,
    });
    this.#pump();
  }

  /** Only a failed card can be dropped, as iOS: one on its way could still land. */
  discard(id: string): void {
    const capture = this.#find(id);
    if (capture?.status !== "failed") return;
    this.#remove(id);
    if (!capture.unsure) return;
    // One whose fate is unknown may be on the lists, and hold other copies back there: they are read again now, and
    // once it lands or is rejected.
    this.#deps.failed(this.scope);
    const scanId = capture.progress.scanId;
    if (scanId !== null) void this.#follow(scanId, this.#deps.now().getTime() + DISCARDED_READ_MS);
  }

  /** A scanner shows the session, until its `closeScanner`. */
  openScanner(): void {
    this.#scanners += 1;
    for (const url of this.#letGone.splice(0)) this.#deps.revokePreview(url);
  }

  /**
   * The last scanner closes: what is ready leaves the tray, and a held front goes; what is on its way stays. Closed
   * only once its picks are read (`later`), a page may already show the cards it lets go: their pictures wait for the
   * next opening, as those of cards that land with no scanner open.
   */
  closeScanner(later = false): void {
    if (this.#scanners > 0) this.#scanners -= 1;
    if (this.#scanners > 0) return;
    const held = this.#heldFront !== null;
    this.#dropHeldFront();
    if (!this.#letGo(!later) && held) this.notifyListeners();
  }

  /**
   * A list shows the scan of a card whose fate was unknown: the scan is read again now, and the card goes as the read
   * says, since a list may show a scan still in the pipeline, or answer from before the pipeline failed. True when it
   * asked; a read that fails leaves the card to Retry.
   */
  listed(id: string): boolean {
    const capture = this.#find(id);
    const scanId = capture?.progress.scanId ?? null;
    if (!capture?.unsure || scanId === null || this.#relisted.has(id)) return false;
    this.#relisted.add(id);
    this.#patch(id, { status: "running", error: null, unsure: false });
    void this.#settle(id, scanId, false);
    return true;
  }

  /** Stops listening (the scans on their way still land server-side) and lets every picture go. */
  override dispose(): void {
    this.#disposed = true;
    for (const controller of this.#listening.values()) controller.abort();
    this.#listening.clear();
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    for (const c of this.#captures) if (c.previewUrl !== null) this.#deps.revokePreview(c.previewUrl);
    for (const url of this.#letGone.splice(0)) this.#deps.revokePreview(url);
    this.#captures = [];
    this.#unlisted.clear();
    this.#relisted.clear();
    this.#dropHeldFront();
    super.dispose();
  }

  /**
   * With no scanner open, what is ready leaves the session: at the last close, and as a card lands after it, as iOS's
   * finish. A card whose list is still read stays until that read answers, so Review has it either way.
   */
  #letGo(atClose = false): boolean {
    if (this.#scanners > 0 || this.#disposed) return false;
    const done = (c: Capture) => c.status === "ready" && !this.#unlisted.has(c.id);
    const gone = this.#captures.filter(done);
    if (gone.length === 0) return false;
    for (const c of gone) {
      if (c.previewUrl === null) continue;
      if (atClose) this.#deps.revokePreview(c.previewUrl);
      else this.#letGone.push(c.previewUrl);
    }
    this.#captures = this.#captures.filter((c) => !done(c));
    this.notifyListeners();
    return true;
  }

  #dropHeldFront(): void {
    if (this.#heldFront !== null) this.#deps.revokePreview(this.#heldFront.previewUrl);
    this.#heldFront = null;
  }

  #enqueue(
    front: Blob,
    back: Blob | null,
    codes: readonly ScanCode[],
    previewUrl = this.#deps.previewUrl(front),
  ): void {
    // The photo a barcode that named nothing waits for: sent with it, on its attempt. Another barcode is another item.
    const waiting = this.awaitingPhoto;
    const same = (a: readonly ScanCode[], b: readonly ScanCode[]) =>
      a.length === b.length && a.every((code, i) => code.type === b[i]?.type && code.value === b[i]?.value);
    if (waiting !== null && (codes.length === 0 || same(codes, waiting.codes))) {
      this.#patch(waiting.id, {
        front,
        back,
        previewUrl,
        status: "waiting",
        progress: initialScanProgress(),
        error: null,
        unsure: false,
        codeOutcome: null,
      });
      this.#pump();
      return;
    }
    this.#push({ front, back, previewUrl, codes });
  }

  #push(o: Pick<Capture, "front" | "back" | "previewUrl" | "codes">): void {
    this.#captures = [
      ...this.#captures,
      {
        id: `capture-${this.#nextId++}`,
        ...o,
        kind: this.#kind,
        codeOutcome: null,
        status: "waiting",
        progress: initialScanProgress(),
        error: null,
        unsure: false,
        attemptId: this.#deps.newAttemptId(),
      },
    ];
    this.notifyListeners();
    this.#pump();
  }

  #pump(): void {
    while (!this.#disposed && this.#streaming < this.#deps.streams) {
      const next = this.#captures.find((c) => c.status === "waiting");
      if (!next) return;
      void this.#send(next);
    }
  }

  async #send(capture: Capture): Promise<void> {
    const id = capture.id;
    const controller = new AbortController();
    this.#streaming += 1;
    this.#listening.set(id, controller);
    this.#patch(id, { status: "running", progress: { ...initialScanProgress(), status: "scanning" } });
    let last = this.#find(id)?.progress ?? initialScanProgress();
    let end: ScanProgress | null = null;
    let refused: string | null = null;
    let gaveUp = false;
    const giveUp = () => {
      gaveUp = true;
      controller.abort();
    };
    let clock = setTimeout(giveUp, STREAM_STARTS_WITHIN_MS);
    let begun = false;
    try {
      end = await this.#deps.run(
        this.scope,
        {
          front: capture.front,
          back: capture.back,
          attemptId: capture.attemptId,
          kind: capture.kind,
          codes: capture.codes,
        },
        (progress) => {
          last = progress;
          if (controller.signal.aborted) return;
          if (!begun && streamBegun(progress)) {
            begun = true;
            clearTimeout(clock);
            clock = setTimeout(giveUp, STREAM_GIVES_UP_MS);
          }
          this.#patch(id, { progress });
        },
        controller.signal,
      );
    } catch (e) {
      end = null;
      refused = e instanceof ScanRefused ? e.message : null;
    } finally {
      clearTimeout(clock);
      this.#streaming -= 1;
      this.#listening.delete(id);
    }
    if (this.#disposed) return;
    const state = end ?? last;
    const outcome = end?.result?.recognitionOutcome === "needs_photo" ? (end.result.codeOutcome ?? "unknown") : null;
    // The barcode named nothing: the attempt stays, for the photo that follows it.
    if (outcome !== null)
      this.#patch(id, { status: "failed", error: codeOutcomeText(outcome), progress: state, codeOutcome: outcome });
    else if (end?.status === "completed" && end.result) this.#land(id, end.result);
    // Cut short, or an error once the scan was made (it may have been saved): the server knows how it ended.
    else if (state.scanId !== null) void this.#settle(id, state.scanId, false, end?.error ?? null);
    // The server ended this attempt's scan: a Retry is a new one.
    else if (end?.status === "error") this.#fail(id, end.error ?? SCAN_DID_NOT_FINISH, end, true);
    // An older backend that ends without a word has done its work, as iOS counts it; a stream given up has not.
    else if (end !== null && !gaveUp) this.#land(id, null);
    // Dropped or refused before the scan began: nothing was made.
    else this.#fail(id, refused ?? SCAN_DID_NOT_FINISH, state);
    this.#pump();
  }

  /**
   * Reads a scan whose stream ended early until it lands or is rejected; a retry sends one that did not land. `said`
   * is the stream's own error, for a scan the read finds rejected.
   */
  async #settle(id: string, scanId: string, retrying: boolean, said: string | null = null): Promise<void> {
    let scan: Scan | "gone";
    try {
      scan = await this.#deps.read(this.scope, scanId);
    } catch {
      if (this.#disposed || !this.#find(id)) return;
      this.#patch(id, { status: "failed", error: SCAN_FATE_UNKNOWN, unsure: true });
      this.#deps.failed(this.scope);
      return;
    }
    if (this.#disposed || !this.#find(id)) return;
    // Deleted by a person, here or on another device (a card the server named had a row): it leaves, never sent again.
    if (scan === "gone") {
      this.#remove(id);
      this.#deps.failed(this.scope);
      return;
    }
    // The pipeline's own reject ends the scan's progress in an error (main's #88, for a scan sent as an attempt, as every
    // card is); one at any other stage was a person's, here or on another device, so the card landed.
    if (scan.review === "REJECTED" && scan.scanProgress !== "scan.error") {
      this.#land(id, scan);
      return;
    }
    // Rejected by the pipeline, the attempt is spent: sending it again would only find that scan.
    if (scan.review === "REJECTED") {
      const attemptId = this.#deps.newAttemptId();
      if (retrying) {
        this.#patch(id, { status: "waiting", progress: initialScanProgress(), error: null, unsure: false, attemptId });
        this.#pump();
      } else {
        this.#patch(id, { status: "failed", error: said ?? SCAN_DID_NOT_FINISH, unsure: false, attemptId });
        this.#deps.failed(this.scope);
      }
      return;
    }
    if (!isScanning(scan, this.#deps.now())) {
      this.#land(id, scan);
      return;
    }
    const timer = setTimeout(() => {
      this.#timers.delete(timer);
      void this.#settle(id, scanId, retrying, said);
    }, SETTLE_READ_MS);
    this.#timers.add(timer);
  }

  /** Reads a discarded scan, with no row for it, until it lands or is rejected, then reads the lists again. */
  async #follow(scanId: string, until: number): Promise<void> {
    const scan = await this.#deps.read(this.scope, scanId).catch(() => null);
    if (this.#disposed) return;
    if (scan === "gone" || scan?.review === "REJECTED") {
      this.#deps.failed(this.scope);
      return;
    }
    if (scan !== null && !isScanning(scan, this.#deps.now())) {
      void Promise.resolve(this.#deps.landed(this.scope, scan)).catch(() => undefined);
      return;
    }
    if (this.#deps.now().getTime() >= until) return;
    const timer = setTimeout(() => {
      this.#timers.delete(timer);
      void this.#follow(scanId, until);
    }, SETTLE_READ_MS);
    this.#timers.add(timer);
  }

  /** Ready once its lists are read with it, or after `LIST_WAIT_MS`: the other way round blinks the card out of them. */
  #land(id: string, scan: Scan | null): void {
    const listed = Promise.resolve(this.#deps.landed(this.scope, scan)).catch(() => undefined);
    this.#unlisted.add(id);
    void listed.then(() => {
      this.#unlisted.delete(id);
      this.#letGo();
    });
    if (scan) this.#patch(id, { progress: this.#landedProgress(id, scan) });
    void Promise.race([listed, this.#after(LIST_WAIT_MS)]).then(() => {
      if (this.#disposed) return;
      this.#patch(id, { status: "ready", error: null, unsure: false });
      this.#letGo();
    });
  }

  /** A wait that sign-out ends. */
  #after(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.#timers.delete(timer);
        resolve();
      }, ms);
      this.#timers.add(timer);
    });
  }

  #landedProgress(id: string, scan: Scan): ScanProgress {
    const progress = this.#find(id)?.progress ?? initialScanProgress();
    return {
      ...progress,
      status: "completed",
      stages: { preparation: "complete", identification: "complete", pricing: "complete", persistence: "complete" },
      scanId: scan.id,
      result: scan,
      error: null,
    };
  }

  #fail(id: string, error: string, progress: ScanProgress, spent = false): void {
    this.#patch(id, {
      status: "failed",
      error,
      unsure: false,
      progress,
      ...(spent ? { attemptId: this.#deps.newAttemptId() } : {}),
    });
    this.#deps.failed(this.scope);
  }

  #find(id: string): Capture | undefined {
    return this.#captures.find((c) => c.id === id);
  }

  #remove(id: string): void {
    const capture = this.#find(id);
    if (!capture) return;
    if (capture.previewUrl !== null) this.#deps.revokePreview(capture.previewUrl);
    this.#relisted.delete(id);
    this.#captures = this.#captures.filter((c) => c.id !== id);
    this.notifyListeners();
  }

  #patch(id: string, change: Partial<Capture>): void {
    if (!this.#find(id)) return;
    this.#captures = this.#captures.map((c) => (c.id === id ? { ...c, ...change } : c));
    this.notifyListeners();
  }
}

/** One session per scanning context for the app: the counter's, and each customer's at it. Reset at sign-out. */
export class ScanSessions {
  readonly #deps: ScanSessionDeps;
  readonly #sessions = new Map<string, ScanSession>();

  constructor(deps: ScanSessionDeps) {
    this.#deps = deps;
  }

  for(scope: ScanScope): ScanSession {
    const key = `${shopEntryId(scope.shopId, scope.locationId)}|${scope.customerId ?? ""}`;
    let session = this.#sessions.get(key);
    if (!session) {
      session = new ScanSession(scope, this.#deps);
      this.#sessions.set(key, session);
    }
    return session;
  }

  reset(): void {
    for (const session of this.#sessions.values()) session.dispose();
    this.#sessions.clear();
  }
}

/** A capture whose scan was rejected, never made, or gone: Retry sends the card again. */
export const SCAN_DID_NOT_FINISH = "The scan did not finish.";

/** A capture whose stream dropped and whose scan could not be read: Retry reads it before sending anything. */
export const SCAN_FATE_UNKNOWN = "Lost touch with the scan. Retry checks whether it landed.";
